import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { Container, Card, CardBody } from "reactstrap";
import { Typography } from "@mui/material";
import moment from "moment";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import LoadingDialog from "../../../components/Common/LoadingDialog";
import Notification from "../../../components/Common/Notification";
import Table from "../../../components/Common/Table";
import CustomButton from "../../../components/Form/CustomButton";
import CustomIconButton from "../../../components/Form/CustomIconButton";
import Label from "../../../components/Common/Label";
import DeleteModal from "../../../components/Common/DeleteModal";
import RenderPopOverButton from "../../../components/Form/RenderPopOverButton.js";
import {
    deleteContentPage,
    duplicateContentPage,
    getContentPages,
} from "../../../store/setting/settingApi";
import "../setting.css";

const ContentPages = ({ userPermission }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editable, setEditable] = useState(false);
    const [deletable, setDeletable] = useState(false);
    const [creatable, setCreatable] = useState(false);
    const [notification, setNotification] = useState({ open: false, msg: "", type: "success" });

    useEffect(() => {
        if (userPermission?.length > 0) {
            userPermission.includes("edit-setting") && setEditable(true);
            userPermission.includes("delete-setting") && setDeletable(true);
            userPermission.includes("edit-setting") && setCreatable(true);
            !userPermission.includes("view-setting") && navigate("/not-authorized");
        }
    }, [userPermission, navigate]);

    const normalizeRows = (payload) => {
        if (!payload) {
            return [];
        }
        if (Array.isArray(payload)) {
            return payload;
        }
        if (Array.isArray(payload.data)) {
            return payload.data;
        }
        return [];
    };

    const loadData = async () => {
        setLoading(true);
        const response = await getContentPages("?paginate=1&size=100&sort=created_at&sort_order=desc");
        if (response?.code === "success") {
            setRows(normalizeRows(response.data));
        } else {
            setRows([]);
            setNotification({
                open: true,
                msg: response?.message || "Failed to load category pages. Check API connection and permissions.",
                type: "error",
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleDuplicate = async (row) => {
        setLoading(true);
        const response = await duplicateContentPage(row.id);
        setNotification({
            open: true,
            msg: response?.message || (response?.code === "success" ? "Duplicated." : "Duplicate failed."),
            type: response?.code === "success" ? "success" : "error",
        });
        await loadData();
    };

    const handleDelete = async () => {
        if (!selectedRow) return;
        setLoading(true);
        const response = await deleteContentPage(selectedRow.id);
        setDeleteOpen(false);
        setNotification({
            open: true,
            msg: response?.message || (response?.code === "success" ? "Deleted." : "Delete failed."),
            type: response?.code === "success" ? "success" : "error",
        });
        await loadData();
    };

    const header = [
        {
            Header: "Title",
            accessor: "title",
            disableFilters: true,
            filterable: false,
        },
        {
            Header: "URL",
            accessor: (cell) => `/${cell.slug}`,
            disableFilters: true,
            filterable: false,
        },
        {
            Header: "Status",
            accessor: (cell) => <Label.StatusByIDLabel label={cell.status} />,
            disableFilters: true,
            filterable: false,
        },
        {
            Header: "Created",
            accessor: (cell) => (
                <Typography style={{ fontFamily: "Manrope", fontSize: 14 }}>
                    {moment(cell.created_at).format("DD MMM YYYY")}
                </Typography>
            ),
            disableFilters: true,
            filterable: false,
        },
        {
            Header: "Action",
            accessor: (cell) => (
                <>
                    {editable && (
                        <Link to={`/settings-content-pages/edit/${cell.id}`}>
                            <CustomIconButton iconName="ri-pencil-line" onClick={() => ""} />
                        </Link>
                    )}
                    <RenderPopOverButton
                        items={[
                            {
                                iconName: "ri-file-copy-line",
                                label: "Duplicate",
                                permission: editable,
                                onClick: () => handleDuplicate(cell),
                            },
                            {
                                iconName: "ri-delete-bin-line",
                                label: "Delete",
                                color: "#ED1A3D",
                                permission: deletable,
                                onClick: () => {
                                    setSelectedRow(cell);
                                    setDeleteOpen(true);
                                },
                            },
                        ]}
                    />
                </>
            ),
            disableFilters: true,
            filterable: false,
            minWidth: 120,
        },
    ];

    return (
        <div className="page-content">
            <LoadingDialog open={loading} />
            <Container fluid>
                <Notification notification={notification} setNotification={setNotification} />
                <Breadcrumb
                    title="Category Pages"
                    backRoute="/settings"
                    breadcrumbItems={[
                        { title: "Settings", link: "/settings" },
                        { title: "Category Pages", link: "/settings-content-pages" },
                    ]}
                />
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="header">Category Pages</div>
                    {creatable && (
                        <CustomButton
                            title="Add Page"
                            press={() => navigate("/settings-content-pages/add")}
                            backgroundColor="#101010"
                            textColor="#FFFFFF"
                            borderStyle="1px solid #101010"
                        />
                    )}
                </div>
                <Card>
                    <CardBody>
                        <Table header={header} data={rows} pageSize={100} />
                    </CardBody>
                </Card>
                <DeleteModal
                    open={deleteOpen}
                    close={() => {
                        setDeleteOpen(false);
                        setSelectedRow(null);
                    }}
                    submit={handleDelete}
                    module="page"
                />
            </Container>
        </div>
    );
};

const mapStateToProps = ({ Login }) => ({
    userPermission: Login.permissionArry,
});

export default connect(mapStateToProps)(ContentPages);
